import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ImagePlus, Zap, Send } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { shrinkImage } from "../lib/helpers";
import { useApp } from "../context/AppContext";
import { Field } from "./Common";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const EMPTY = { name: "", category: "General", subject: "", body: "", service_ids: [] };
const KIND_LABEL = { welcome: "Sent automatically when someone first subscribes to the newsletter", birthday: "Sent automatically on the patient's birthday" };

function TplPhoto({ t, onDone }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { await api.put(`/msg-templates/${t.id}/photo`, { photo: await shrinkImage(f) }); toast.success("Photo added"); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = () => api.delete(`/msg-templates/${t.id}/photo`).then(() => { toast.success("Photo removed"); onDone(); });
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={`tpl-photo-${t.id}`}>
      {t.photo_url && <img src={t.photo_url} alt="" className="h-16 w-28 rounded-md object-cover border" data-testid={`tpl-photo-img-${t.id}`} />}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`tpl-photo-input-${t.id}`} onChange={pick} />
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`tpl-photo-add-${t.id}`} onClick={() => ref.current.click()}><ImagePlus className="h-4 w-4 mr-1" />{t.photo_url ? "Change photo" : "Add email photo"}</Button>
      {t.photo_url && <Button size="sm" variant="ghost" data-testid={`tpl-photo-remove-${t.id}`} onClick={remove}>Remove photo</Button>}
      {!t.photo_url && <span className="text-xs text-muted-foreground">Shown at the top of the email, under your logo</span>}
    </div>
  );
}

function ServiceLinks({ value, onChange, id }) {
  const { services } = useApp();
  const toggle = (sid) => onChange(value.includes(sid) ? value.filter((x) => x !== sid) : [...value, sid]);
  return (
    <div className="space-y-1" data-testid={`tpl-services-${id}`}>
      <p className="text-xs text-muted-foreground">Send automatically (email + in-app) when one of these treatments is marked Completed:</p>
      <div className="flex flex-wrap gap-1.5">{services.map((s) => (
        <button type="button" key={s.id} data-testid={`tpl-service-${id}-${s.id}`} onClick={() => toggle(s.id)}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${value.includes(s.id) ? "bg-primary text-primary-foreground border-primary" : "hover:border-primary"}`}>{s.name}</button>))}</div>
    </div>
  );
}

function TemplateRow({ t, onDone }) {
  const [f, setF] = useState(t);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isNew = !t.id;
  const save = () => (isNew ? api.post("/msg-templates", f) : api.put(`/msg-templates/${t.id}`, { name: f.name, category: f.category, subject: f.subject, body: f.body, service_ids: f.service_ids || [] }))
    .then(() => { toast.success("Template saved"); onDone(); if (isNew) setF(EMPTY); }).catch((e) => toast.error(errMsg(e)));
  const del = () => window.confirm("Delete this template?") && api.delete(`/msg-templates/${t.id}`).then(onDone).catch((e) => toast.error(errMsg(e)));
  const test = () => api.post(`/msg-templates/${t.id}/test`, { subject: f.subject, body: f.body }, { timeout: 60000 })
    .then((r) => toast.success(`Test email sent to ${r.data.sent_to}`)).catch((e) => toast.error(errMsg(e)));
  const id = t.id || "new";
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 space-y-2" data-testid={`tpl-${id}`}>
      {t.kind && <p className="text-xs font-semibold text-primary flex items-center gap-1" data-testid={`tpl-kind-${id}`}><Zap className="h-3.5 w-3.5" />{KIND_LABEL[t.kind]} · also use {"{code}"}, {"{percent}"}, {"{expiry}"} — the code and a “Book with my code” button are always added below your text</p>}
      <div className="grid sm:grid-cols-3 gap-2">
        <Input data-testid={`tpl-name-${id}`} placeholder="Template name" value={f.name} onChange={set("name")} />
        <Input data-testid={`tpl-category-${id}`} placeholder="Category (e.g. Aftercare)" value={f.category} onChange={set("category")} />
        <Input data-testid={`tpl-subject-${id}`} placeholder="Email subject (optional)" value={f.subject} onChange={set("subject")} />
      </div>
      <Textarea rows={4} data-testid={`tpl-body-${id}`} placeholder="Message text" value={f.body} onChange={set("body")} />
      {!t.kind && <ServiceLinks id={id} value={f.service_ids || []} onChange={(v) => setF({ ...f, service_ids: v })} />}
      {!isNew && <TplPhoto t={t} onDone={onDone} />}
      <div className="flex gap-2">
        <Button size="sm" className="rounded-full" data-testid={`tpl-save-${id}`} disabled={!f.name.trim() || !f.body.trim()} onClick={save}>{isNew ? <><Plus className="h-4 w-4 mr-1" />Add template</> : "Save"}</Button>
        {!isNew && <Button size="sm" variant="outline" className="rounded-full" data-testid={`tpl-test-${id}`} disabled={!f.body.trim()} onClick={test}><Send className="h-4 w-4 mr-1" />Send me a test</Button>}
        {!isNew && !t.kind && <Button size="sm" variant="ghost" data-testid={`tpl-delete-${id}`} onClick={del}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
      </div>
    </div>
  );
}

function FollowupSettings({ templates }) {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/settings/followup").then((r) => setF(r.data)); }, []);
  if (!f) return null;
  const save = () => api.put("/settings/followup", { ...f, days: Number(f.days) }).then(() => toast.success("Saved")).catch((e) => toast.error(errMsg(e)));
  const run = () => api.post("/settings/followup/run").then((r) => toast.success(`${r.data.sent} check-in(s) sent`)).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-[var(--radius)] border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="followup-settings">
      <div className="flex flex-wrap items-center gap-3">
        <Switch data-testid="followup-enabled" checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} />
        <span className="font-semibold text-sm">“How are you feeling?” check-in</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Days after the treatment"><Input type="number" min={1} max={60} className="w-24" data-testid="followup-days" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} /></Field>
        <Field label="Template"><select className="h-10 rounded-md border bg-background px-3 text-sm" data-testid="followup-template" value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })}>
          <option value="">Choose…</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
        <Button size="sm" className="rounded-full" data-testid="followup-save" onClick={save}>Save</Button>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="followup-run" onClick={run}>Send due check-ins now</Button>
      </div>
      <p className="text-xs text-muted-foreground">Sent from 10:00 clinic time by email, in-app message and WhatsApp (if the follow-up WhatsApp template is set), with a “Book again” button for the same treatment and location.</p>
    </div>
  );
}

export function TemplateSettings() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/msg-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const clear = () => window.confirm("Remove all templates and start empty?") && api.delete("/msg-templates").then(() => { toast.success("All templates removed"); load(); });
  if (!items) return null;
  return (
    <div className="max-w-3xl space-y-4" data-testid="template-settings">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">Ready-made messages for WhatsApp, email and in-app chat. Use <code>{"{first_name}"}</code>, <code>{"{next_appointment}"}</code> and <code>{"{clinic_name}"}</code> — they're filled in automatically. Starter aftercare texts are general: please adapt them to your own protocols.</p>
        {items.length > 0 && <Button size="sm" variant="outline" className="rounded-full" data-testid="tpl-clear-all" onClick={clear}>Start empty</Button>}
      </div>
      <FollowupSettings templates={items} />
      <Field label="New template"><TemplateRow t={EMPTY} onDone={load} /></Field>
      {items.map((t) => <TemplateRow key={t.id} t={t} onDone={load} />)}
    </div>
  );
}
